import { defineStore } from "pinia";
import type { ISoapClient } from "@/soapClient";
import { useSoapClient } from "@/soapClient";
// https://vueuse.org/core/uselocalstorage/
import { useLocalStorage } from "@vueuse/core";
import type { RemovableRef } from "@vueuse/shared";
import type { Impostazioni } from "@/types";
import { useRootStore } from "@/stores/root";
import { loadConfigs } from "@/utils";

const ls = useLocalStorage;
type ImpostazioniTipiDocumento = { [IdDoc: string]: Impostazioni };

export type SettingsState = {
  WS_BASE_URL: RemovableRef<string>;
  DDT_BASE_URL: RemovableRef<string>;
  IMPOSTAZIONI_TIPI_DOCUMENTO: RemovableRef<ImpostazioniTipiDocumento>;
  STATS: RemovableRef<boolean>;
  STATS_FREQUENCY_DAY: RemovableRef<number>;
  STATS_FREQUENCY_WEEK: RemovableRef<number>;
  STATS_FREQUENCY_MONTH: RemovableRef<number>;
};
export const useSettingsStore = defineStore({
  id: "settingStore",
  state: (): SettingsState => ({
    WS_BASE_URL: ls<string>("isales/settingStore/WS_BASE_URL", "./isales.asmx"),
    DDT_BASE_URL: ls<string>("isales/settingStore/DDT_BASE_URL", "./ddt.ashx"),
    IMPOSTAZIONI_TIPI_DOCUMENTO: ls<ImpostazioniTipiDocumento>("isales/settingStore/IMPOSTAZIONI_TIPI_DOCUMENTO", {}),
    STATS: ls<boolean>("isales/settingStore/STATS", false),
    STATS_FREQUENCY_DAY: ls<number>("isales/settingStore/STATS_FREQUENCY_DAY", 60 * 60),
    STATS_FREQUENCY_WEEK: ls<number>("isales/settingStore/STATS_FREQUENCY_WEEK", 60 * 60 * 6),
    STATS_FREQUENCY_MONTH: ls<number>("isales/settingStore/STATS_FREQUENCY_MONTH", 60 * 60 * 12),
  }),
  getters: {
    wsClient(state): ISoapClient {
      return useSoapClient({ baseUrl: state.WS_BASE_URL });
    },
    DocType_Impostazione_get(
      state
    ): <K extends keyof Impostazioni, T extends Impostazioni[K]>(idDoc: string, nomeImpostazione: K) => T | undefined {
      return <K extends keyof Impostazioni, T extends Impostazioni[K]>(idDoc: string, nomeImpostazione: K) => {
        const docSettings: Impostazioni = state.IMPOSTAZIONI_TIPI_DOCUMENTO[idDoc];
        if (docSettings) {
          return docSettings[nomeImpostazione] as T;
        }
      };
    },
    ColonneVisibiliOrdine_get(): (idDoc: string, mostraDatiRiservati: boolean) => string[] | undefined {
      return (idDoc, mostraDatiRiservati) => {
        const cols = this.DocType_Impostazione_get(idDoc, "ColonneVisibiliOrdine");
        if (!cols) return undefined;
        // filtra le colonne da non visulizzare se richiesto
        const filtered = cols.filter((col) => mostraDatiRiservati || col !== "vecpzunilis") || {};
        return [...filtered, ...["actions"]]; // aggiunge sempre la colonna "actions" per visualizzare pulsanti
      };
    },
    ControllaGiacenzaOrdine_get(): (idDoc: string) => boolean | undefined {
      return (idDoc) => this.DocType_Impostazione_get(idDoc, "ControllaGiacenzaOrdine");
    },
    AbilitaTastoCorrispettivo_get(): (idDoc: string) => boolean | undefined {
      return (idDoc) => this.DocType_Impostazione_get(idDoc, "AbilitaTastoCorrispettivo");
    },
    NotaDiDefault_get(): (idDoc: string) => string | undefined {
      return (idDoc) => this.DocType_Impostazione_get(idDoc, "NotaDiDefault");
    },
    RichiediFirma_get(): (idDoc: string) => boolean {
      return (idDoc) => !!this.DocType_Impostazione_get(idDoc, "RichiediFirma");
    },
    Label_get(): (idDoc: string) => string | undefined {
      return (idDoc) => this.DocType_Impostazione_get(idDoc, "Label");
    },
    ControlloFido_get(): (idDoc: string) => boolean | undefined {
      return (idDoc) => this.DocType_Impostazione_get(idDoc, "ControlloFido");
    },
    ConsentiSfusi_get(): (idDoc: string) => boolean | undefined {
      return (idDoc) => this.DocType_Impostazione_get(idDoc, "ConsentiSfusi");
    },
    FormulaSconti_get(): (idDoc: string) => string | undefined {
      return (idDoc) => this.DocType_Impostazione_get(idDoc, "FormulaSconti");
    },
    FormulaScontiUnitario_get(): (idDoc: string) => string | undefined {
      return (idDoc) => this.DocType_Impostazione_get(idDoc, "FormulaScontiUnitario");
    },
    IMPOSTAZIONI_TIPI_DOCUMENTO_get(state) {
      return state.IMPOSTAZIONI_TIPI_DOCUMENTO;
    },
    DDT_URL_get(state): (id: number, accessToken: string, tipoDocumento?: string) => string {
      const rootStore = useRootStore();
      return (id, accessToken, tipoDocumento: string | undefined = undefined) => `${state.DDT_BASE_URL}?d=${id}&t=${rootStore.AUTH_TOKEN_get}&a=${accessToken}` + (tipoDocumento ? `&f=${tipoDocumento}` : '');
    },
    STATS_get(state) {
      return state.STATS;
    }
  },
  actions: {
    WS_BASE_URL_set(url: string): void {
      this.WS_BASE_URL = url;
    },
    DDT_BASE_URL_set(url: string) {
      this.DDT_BASE_URL = url;
    },
    STATS_set(value: boolean) {
      this.STATS = value;
    },
    async load() {
      const responses = await loadConfigs();
      if (responses.length === 0) {
        throw "Errore nel caricamento del file di configurazione";
      }
      const config = responses[0].data;
      this.WS_BASE_URL_set(config.wsUrl);
      this.DDT_BASE_URL_set(config.ddtUrl);
      this.STATS_set(config.stats ?? false);
      const response = await this.wsClient.GetImpostazioni();
      if (!response) {
        throw "Errore nel caricamento delle impostazioni";
      }
      if (!response.Success) {
        throw response.Message;
      }
      const settings: ImpostazioniTipiDocumento = {};
      for (const s of response.Data) {
        settings[s.IdDoc] = s;
      }
      this.IMPOSTAZIONI_TIPI_DOCUMENTO = settings;
    },
  },
});
